import React, { useEffect, useState } from 'react';
import {
  Shield,
  Database,
  Activity,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Wrench,
  HardDrive,
  Users,
  BookOpen,
  Package,
  X,
  Play,
  Eye,
  Download,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Link2,
  Unlink
} from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import StatsCard from '@/components/admin/StatsCard';
import Modal from '@/components/admin/Modal';
import adminHealthService from '@/services/adminHealth.service';
import { authService } from '@/services';
import type {
  HealthCheckResult,
  RepairResult,
  BundleRepairResult
} from '@/services/adminHealth.service';

export default function AdminDatabaseIntegrity() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Health check state
  const [healthData, setHealthData] = useState<HealthCheckResult | null>(null);
  const [lastCheckTime, setLastCheckTime] = useState<Date | null>(null);
  const [checkingHealth, setCheckingHealth] = useState(false);

  // Repair state
  const [repairModalOpen, setRepairModalOpen] = useState(false);
  const [repairType, setRepairType] = useState<'enrollments' | 'bundles' | null>(null);
  const [repairPreview, setRepairPreview] = useState<RepairResult | BundleRepairResult | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [executingRepair, setExecutingRepair] = useState(false);

  // Backup state
  const [backupModalOpen, setBackupModalOpen] = useState(false);
  const [backupLabel, setBackupLabel] = useState('');
  const [creatingBackup, setCreatingBackup] = useState(false);

  // Expanded sections
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    enrollments: true,
    curriculum: false,
    bundles: false,
    users: false
  });

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
    runHealthCheck();
  }, []);

  const runHealthCheck = async () => {
    setCheckingHealth(true);
    setError(null);
    try {
      const response = await adminHealthService.runHealthCheck();
      if (!response.success) {
        throw new Error(response.error || 'Failed to run health check');
      }
      setHealthData(response.data || null);
      setLastCheckTime(new Date());
    } catch (error: any) {
      console.error('Error running health check:', error);
      setError(error.message || 'Failed to run health check');
    } finally {
      setCheckingHealth(false);
      setLoading(false);
    }
  };

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case 'HEALTHY':
        return 'text-green-400';
      case 'WARNING':
        return 'text-yellow-400';
      case 'CRITICAL':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  const getHealthStatusBg = (status: string) => {
    switch (status) {
      case 'HEALTHY':
        return 'bg-green-500/20 border-green-500/30';
      case 'WARNING':
        return 'bg-yellow-500/20 border-yellow-500/30';
      case 'CRITICAL':
        return 'bg-red-500/20 border-red-500/30';
      default:
        return 'bg-gray-500/20 border-gray-500/30';
    }
  };

  const getHealthIcon = (status: string) => {
    switch (status) {
      case 'HEALTHY':
        return <CheckCircle2 className="w-6 h-6 text-green-400" />;
      case 'WARNING':
        return <AlertTriangle className="w-6 h-6 text-yellow-400" />;
      case 'CRITICAL':
        return <XCircle className="w-6 h-6 text-red-400" />;
      default:
        return <Activity className="w-6 h-6 text-gray-400" />;
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const openRepairModal = (type: 'enrollments' | 'bundles') => {
    setRepairType(type);
    setRepairPreview(null);
    setRepairModalOpen(true);
  };

  const loadRepairPreview = async () => {
    if (!repairType) return;

    setLoadingPreview(true);
    setError(null);
    try {
      let response;
      if (repairType === 'enrollments') {
        response = await adminHealthService.repairEnrollmentsBySlug(true);
      } else {
        response = await adminHealthService.fixBundleCourseIds(true);
      }

      if (!response.success) {
        throw new Error(response.error || 'Failed to load preview');
      }
      setRepairPreview(response.data || null);
    } catch (error: any) {
      console.error('Error loading preview:', error);
      setError(error.message || 'Failed to load repair preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const executeRepair = async () => {
    if (!repairType) return;

    setExecutingRepair(true);
    setError(null);
    try {
      let response;
      if (repairType === 'enrollments') {
        response = await adminHealthService.repairEnrollmentsBySlug(false);
      } else {
        response = await adminHealthService.fixBundleCourseIds(false);
      }

      if (!response.success) {
        throw new Error(response.error || 'Repair failed');
      }

      setSuccessMessage(`Successfully repaired ${repairType}!`);
      setRepairModalOpen(false);
      setRepairPreview(null);

      // Refresh health check
      await runHealthCheck();

      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error: any) {
      console.error('Error executing repair:', error);
      setError(error.message || 'Repair operation failed');
    } finally {
      setExecutingRepair(false);
    }
  };

  const createBackup = async () => {
    setCreatingBackup(true);
    setError(null);
    try {
      const response = await adminHealthService.createBackup(backupLabel || undefined);
      if (!response.success) {
        throw new Error(response.error || 'Failed to create backup');
      }

      setSuccessMessage('Backup created successfully!');
      setBackupModalOpen(false);
      setBackupLabel('');

      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error: any) {
      console.error('Error creating backup:', error);
      setError(error.message || 'Failed to create backup');
    } finally {
      setCreatingBackup(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout>
      <section className="min-h-screen bg-gradient-to-br from-purple-900 via-black to-blue-900 py-12 px-4">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">
                Database Integrity
              </h1>
              <p className="text-gray-400">Monitor health, repair issues, and manage backups</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setBackupModalOpen(true)}
                className="px-4 py-2 bg-green-600/20 border border-green-500/30 text-green-400 rounded-lg hover:bg-green-600/30 transition-all flex items-center space-x-2"
              >
                <HardDrive className="w-4 h-4" />
                <span>Create Backup</span>
              </button>
              <div className="flex items-center space-x-2 text-sm text-gray-400">
                <Shield className="w-4 h-4" />
                <span>Admin Access</span>
              </div>
            </div>
          </div>

          {/* Success/Error Messages */}
          {successMessage && (
            <div className="bg-green-500/20 border border-green-500/30 text-green-400 px-6 py-4 rounded-lg flex items-center space-x-2">
              <CheckCircle2 className="w-5 h-5" />
              <span>{successMessage}</span>
            </div>
          )}

          {error && (
            <div className="bg-red-500/20 border border-red-500/30 text-red-400 px-6 py-4 rounded-lg flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <XCircle className="w-5 h-5" />
                <span>{error}</span>
              </div>
              <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
                <X className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Overall Health Status */}
          {loading ? (
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
              <p className="mt-4 text-gray-400">Running health check...</p>
            </div>
          ) : healthData ? (
            <>
              {/* Health Status Header */}
              <div className={`${getHealthStatusBg(healthData.overall_health)} border rounded-2xl p-6`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {getHealthIcon(healthData.overall_health)}
                    <div>
                      <h2 className={`text-2xl font-bold ${getHealthStatusColor(healthData.overall_health)}`}>
                        Database Status: {healthData.overall_health}
                      </h2>
                      <p className="text-gray-400 text-sm mt-1">
                        {healthData.issues_found === 0
                          ? 'All systems operational'
                          : `${healthData.issues_found} issue${healthData.issues_found > 1 ? 's' : ''} detected`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    {lastCheckTime && (
                      <div className="text-sm text-gray-400 flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>Last check: {lastCheckTime.toLocaleTimeString()}</span>
                      </div>
                    )}
                    <button
                      onClick={runHealthCheck}
                      disabled={checkingHealth}
                      className="px-4 py-2 bg-white/10 rounded-lg text-white hover:bg-white/20 transition-all flex items-center space-x-2 disabled:opacity-50"
                    >
                      <RefreshCw className={`w-4 h-4 ${checkingHealth ? 'animate-spin' : ''}`} />
                      <span>{checkingHealth ? 'Checking...' : 'Refresh'}</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatsCard
                  label="Total Enrollments"
                  value={healthData.checks.enrollments.total}
                  icon={Users}
                  iconColor="text-purple-400"
                />
                <StatsCard
                  label="Total Courses"
                  value={healthData.checks.course_curriculum.total_courses}
                  icon={BookOpen}
                  iconColor="text-blue-400"
                />
                <StatsCard
                  label="Total Bundles"
                  value={healthData.checks.bundles.total_bundles}
                  icon={Package}
                  iconColor="text-green-400"
                />
                <StatsCard
                  label="Issues Found"
                  value={healthData.issues_found}
                  icon={healthData.issues_found > 0 ? AlertTriangle : CheckCircle2}
                  iconColor={healthData.issues_found > 0 ? 'text-yellow-400' : 'text-green-400'}
                />
              </div>

              {/* Detailed Health Checks */}
              <div className="space-y-4">
                {/* Enrollments Section */}
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
                  <button
                    onClick={() => toggleSection('enrollments')}
                    className="w-full p-6 flex items-center justify-between hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <Users className="w-6 h-6 text-purple-400" />
                      <div className="text-left">
                        <h3 className="text-xl font-bold text-white">Enrollment Integrity</h3>
                        <p className="text-sm text-gray-400">
                          {healthData.checks.enrollments.total} enrollments checked
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getHealthStatusBg(healthData.checks.enrollments.status)} ${getHealthStatusColor(healthData.checks.enrollments.status)}`}>
                        {healthData.checks.enrollments.status}
                      </span>
                    </div>
                    {expandedSections.enrollments ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                  </button>

                  {expandedSections.enrollments && (
                    <div className="px-6 pb-6 border-t border-white/10">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                        <div className="bg-black/40 rounded-xl p-4">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400">Orphaned Enrollments</span>
                            <span className={`text-2xl font-bold ${healthData.checks.enrollments.orphaned > 0 ? 'text-red-400' : 'text-green-400'}`}>
                              {healthData.checks.enrollments.orphaned}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">Enrollments pointing to missing products</p>
                        </div>
                        <div className="bg-black/40 rounded-xl p-4">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400">Missing Slug</span>
                            <span className={`text-2xl font-bold ${healthData.checks.enrollments.missing_slug > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
                              {healthData.checks.enrollments.missing_slug}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">Enrollments without product_slug field</p>
                        </div>
                        <div className="bg-black/40 rounded-xl p-4">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400">Missing Product ID</span>
                            <span className={`text-2xl font-bold ${healthData.checks.enrollments.missing_product_id > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
                              {healthData.checks.enrollments.missing_product_id}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">Enrollments without product_id field</p>
                        </div>
                      </div>

                      {(healthData.checks.enrollments.orphaned > 0 || healthData.checks.enrollments.missing_slug > 0) && (
                        <div className="mt-4 flex justify-end">
                          <button
                            onClick={() => openRepairModal('enrollments')}
                            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white transition-all flex items-center space-x-2"
                          >
                            <Wrench className="w-4 h-4" />
                            <span>Repair Enrollments</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Course-Curriculum Section */}
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
                  <button
                    onClick={() => toggleSection('curriculum')}
                    className="w-full p-6 flex items-center justify-between hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <Link2 className="w-6 h-6 text-blue-400" />
                      <div className="text-left">
                        <h3 className="text-xl font-bold text-white">Course-Curriculum Links</h3>
                        <p className="text-sm text-gray-400">
                          {healthData.checks.course_curriculum.total_courses} courses, {healthData.checks.course_curriculum.total_curriculums} curriculums
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getHealthStatusBg(healthData.checks.course_curriculum.status)} ${getHealthStatusColor(healthData.checks.course_curriculum.status)}`}>
                        {healthData.checks.course_curriculum.status}
                      </span>
                    </div>
                    {expandedSections.curriculum ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                  </button>

                  {expandedSections.curriculum && (
                    <div className="px-6 pb-6 border-t border-white/10">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div className="bg-black/40 rounded-xl p-4">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400">Courses Without Curriculum</span>
                            <span className={`text-2xl font-bold ${healthData.checks.course_curriculum.courses_without_curriculum > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
                              {healthData.checks.course_curriculum.courses_without_curriculum}
                            </span>
                          </div>
                        </div>
                        <div className="bg-black/40 rounded-xl p-4">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400">Orphaned Curriculums</span>
                            <span className={`text-2xl font-bold ${healthData.checks.course_curriculum.orphaned_curriculums > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
                              {healthData.checks.course_curriculum.orphaned_curriculums}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Bundles Section */}
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
                  <button
                    onClick={() => toggleSection('bundles')}
                    className="w-full p-6 flex items-center justify-between hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <Package className="w-6 h-6 text-green-400" />
                      <div className="text-left">
                        <h3 className="text-xl font-bold text-white">Bundle References</h3>
                        <p className="text-sm text-gray-400">
                          {healthData.checks.bundles.total_bundles} bundles checked
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getHealthStatusBg(healthData.checks.bundles.status)} ${getHealthStatusColor(healthData.checks.bundles.status)}`}>
                        {healthData.checks.bundles.status}
                      </span>
                    </div>
                    {expandedSections.bundles ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                  </button>

                  {expandedSections.bundles && (
                    <div className="px-6 pb-6 border-t border-white/10">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div className="bg-black/40 rounded-xl p-4">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400">Invalid Course References</span>
                            <span className={`text-2xl font-bold ${healthData.checks.bundles.invalid_course_refs > 0 ? 'text-red-400' : 'text-green-400'}`}>
                              {healthData.checks.bundles.invalid_course_refs}
                            </span>
                          </div>
                        </div>
                        <div className="bg-black/40 rounded-xl p-4">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400">Empty Bundles</span>
                            <span className={`text-2xl font-bold ${healthData.checks.bundles.empty_bundles > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
                              {healthData.checks.bundles.empty_bundles}
                            </span>
                          </div>
                        </div>
                      </div>

                      {healthData.checks.bundles.invalid_course_refs > 0 && (
                        <div className="mt-4 flex justify-end">
                          <button
                            onClick={() => openRepairModal('bundles')}
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white transition-all flex items-center space-x-2"
                          >
                            <Wrench className="w-4 h-4" />
                            <span>Fix Bundle References</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Users Section */}
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
                  <button
                    onClick={() => toggleSection('users')}
                    className="w-full p-6 flex items-center justify-between hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <Users className="w-6 h-6 text-cyan-400" />
                      <div className="text-left">
                        <h3 className="text-xl font-bold text-white">User Accounts</h3>
                        <p className="text-sm text-gray-400">
                          {healthData.checks.users.total_users} users, {healthData.checks.users.admin_users} admins
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getHealthStatusBg(healthData.checks.users.status)} ${getHealthStatusColor(healthData.checks.users.status)}`}>
                        {healthData.checks.users.status}
                      </span>
                    </div>
                    {expandedSections.users ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                  </button>

                  {expandedSections.users && (
                    <div className="px-6 pb-6 border-t border-white/10">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div className="bg-black/40 rounded-xl p-4">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400">Total Users</span>
                            <span className="text-2xl font-bold text-white">
                              {healthData.checks.users.total_users}
                            </span>
                          </div>
                        </div>
                        <div className="bg-black/40 rounded-xl p-4">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400">Admin Users</span>
                            <span className="text-2xl font-bold text-purple-400">
                              {healthData.checks.users.admin_users}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Recommendations */}
              {healthData.recommendations && healthData.recommendations.length > 0 && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-6">
                  <h3 className="text-xl font-bold text-yellow-400 mb-4 flex items-center space-x-2">
                    <AlertCircle className="w-5 h-5" />
                    <span>Recommendations</span>
                  </h3>
                  <ul className="space-y-2">
                    {healthData.recommendations.map((rec, index) => (
                      <li key={index} className="text-gray-300 flex items-start space-x-2">
                        <span className="text-yellow-400">•</span>
                        <span>{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-12 text-center">
              <Database className="w-16 h-16 mx-auto mb-4 text-gray-600" />
              <p className="text-xl text-gray-400">No health data available</p>
              <button
                onClick={runHealthCheck}
                className="mt-4 px-6 py-3 bg-purple-600 rounded-lg text-white hover:bg-purple-700 transition-all"
              >
                Run Health Check
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Repair Modal */}
      <Modal
        isOpen={repairModalOpen}
        onClose={() => {
          setRepairModalOpen(false);
          setRepairPreview(null);
        }}
        title={`Repair ${repairType === 'enrollments' ? 'Enrollments' : 'Bundle References'}`}
      >
        <div className="space-y-6">
          {!repairPreview ? (
            <div className="text-center py-8">
              <Wrench className="w-16 h-16 mx-auto mb-4 text-purple-400" />
              <h3 className="text-xl font-bold text-white mb-2">
                {repairType === 'enrollments' ? 'Repair Orphaned Enrollments' : 'Fix Bundle Course References'}
              </h3>
              <p className="text-gray-400 mb-6">
                {repairType === 'enrollments'
                  ? 'This will attempt to repair orphaned enrollments using the product_slug field to find the correct product.'
                  : 'This will remove invalid course references from bundles.'}
              </p>
              <button
                onClick={loadRepairPreview}
                disabled={loadingPreview}
                className="px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg text-white transition-all flex items-center space-x-2 mx-auto disabled:opacity-50"
              >
                <Eye className="w-4 h-4" />
                <span>{loadingPreview ? 'Loading Preview...' : 'Preview Changes'}</span>
              </button>
            </div>
          ) : (
            <div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
                <h4 className="text-blue-400 font-semibold mb-2">Dry Run Results</h4>
                {repairType === 'enrollments' ? (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-400">Total Checked:</span>
                      <span className="text-white ml-2">{(repairPreview as RepairResult).total_checked}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Will Repair:</span>
                      <span className="text-green-400 ml-2">{(repairPreview as RepairResult).repaired}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Will Skip:</span>
                      <span className="text-gray-400 ml-2">{(repairPreview as RepairResult).skipped}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Will Fail:</span>
                      <span className="text-red-400 ml-2">{(repairPreview as RepairResult).failed}</span>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-400">Total Bundles:</span>
                      <span className="text-white ml-2">{(repairPreview as BundleRepairResult).total_bundles}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Bundles to Fix:</span>
                      <span className="text-green-400 ml-2">{(repairPreview as BundleRepairResult).bundles_fixed}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-400">Invalid Refs to Remove:</span>
                      <span className="text-yellow-400 ml-2">{(repairPreview as BundleRepairResult).total_invalid_refs_removed}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Details list */}
              {repairPreview && 'details' in repairPreview && repairPreview.details.length > 0 && (
                <div className="max-h-64 overflow-y-auto bg-black/40 rounded-lg p-4 mb-4">
                  <h5 className="text-gray-400 text-sm mb-2">Details:</h5>
                  <div className="space-y-2">
                    {repairPreview.details.slice(0, 20).map((detail: any, index: number) => (
                      <div key={index} className="text-sm flex items-center space-x-2">
                        {detail.status === 'repaired' || detail.status === 'fixed' ? (
                          <CheckCircle2 className="w-4 h-4 text-green-400" />
                        ) : detail.status === 'failed' ? (
                          <XCircle className="w-4 h-4 text-red-400" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-gray-400" />
                        )}
                        <span className="text-gray-300 truncate">{detail.message || detail.bundle_title || detail.enrollment_id}</span>
                      </div>
                    ))}
                    {repairPreview.details.length > 20 && (
                      <p className="text-gray-500 text-xs">...and {repairPreview.details.length - 20} more</p>
                    )}
                  </div>
                </div>
              )}

              <div className="flex space-x-4">
                <button
                  onClick={executeRepair}
                  disabled={executingRepair}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg font-semibold hover:shadow-xl transition-all disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  <Play className="w-4 h-4" />
                  <span>{executingRepair ? 'Executing...' : 'Execute Repair'}</span>
                </button>
                <button
                  onClick={() => {
                    setRepairModalOpen(false);
                    setRepairPreview(null);
                  }}
                  disabled={executingRepair}
                  className="px-6 py-3 bg-white/10 rounded-lg text-gray-400 hover:bg-white/20 transition-all disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Backup Modal */}
      <Modal
        isOpen={backupModalOpen}
        onClose={() => setBackupModalOpen(false)}
        title="Create Database Backup"
      >
        <div className="space-y-6">
          <div className="text-center py-4">
            <HardDrive className="w-16 h-16 mx-auto mb-4 text-green-400" />
            <p className="text-gray-400 mb-4">
              Create a full backup of all database collections. Backups are stored locally and can be used for disaster recovery.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Backup Label (optional)
            </label>
            <input
              type="text"
              value={backupLabel}
              onChange={(e) => setBackupLabel(e.target.value)}
              placeholder="e.g., pre-deployment, before-migration"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500/50 focus:outline-none"
            />
          </div>

          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">Collections to backup:</h4>
            <div className="grid grid-cols-2 gap-2 text-sm text-gray-300">
              <span>• enrollments</span>
              <span>• users</span>
              <span>• courses</span>
              <span>• bundles</span>
              <span>• course_curriculums</span>
              <span>• stashed_enrollments</span>
              <span>• site_videos</span>
              <span>• messages</span>
            </div>
          </div>

          <div className="flex space-x-4">
            <button
              onClick={createBackup}
              disabled={creatingBackup}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg font-semibold hover:shadow-xl transition-all disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>{creatingBackup ? 'Creating Backup...' : 'Create Backup'}</span>
            </button>
            <button
              onClick={() => setBackupModalOpen(false)}
              disabled={creatingBackup}
              className="px-6 py-3 bg-white/10 rounded-lg text-gray-400 hover:bg-white/20 transition-all disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </AdminLayout>
  );
}
